import { Outlet } from 'react-router-dom';
import { ProjectsProvider } from '../../projects/ProjectsProvider';
import { VocabularyProvider } from '../../vocab/VocabularyProvider';
import { BottomTabBar } from './BottomTabBar';
import { ShellChromeContext } from './chrome-context';
import { ProjectRail } from './ProjectRail';
import { ProjectSwitcher } from './ProjectSwitcher';
import { TopBar } from './TopBar';
import { ViewNav } from './ViewNav';
import { useProjectSelection } from './nav-links';

/**
 * 🔴 THE SHELL — and the two SHAPES it takes, which are not one layout at two widths.
 *
 * ┌ ≥768px ────────────────────────────────┐   ┌ <768px (390×844) ─────────┐
 * │ TopBar: mark · Backlog List Board Ver… │   │ TopBar: mark · [TBS ▾]    │
 * ├─────────┬──────────────────────────────┤   ├───────────────────────────┤
 * │ Project │                              │   │                           │
 * │ rail    │        <Outlet />            │   │        <Outlet />         │
 * │ 240px   │                              │   │                           │
 * └─────────┴──────────────────────────────┘   ├───────────────────────────┤
 *                                              │ ☰ Backlog ▤ List ▦ … ⚑    │
 *                                              └───────────────────────────┘
 *
 * ⭐ WHY THEY DIFFER. On a desktop the pointer is equidistant from everywhere, so the thing you
 * switch RARELY (project) can afford a permanent 240px column and the thing you switch OFTEN (view)
 * belongs in the top chrome. On a phone held in one hand the geometry inverts: the bottom third is
 * free and the top is a stretch, so the OFTEN thing goes to the bottom as a tab bar and the RARELY
 * thing becomes a compact control in the top bar that still shows its current value.
 *
 * ⛔ What we did not do: shrink the rail into a drawer. That is the "same layout, smaller" answer,
 * and it costs a discovery tap on the navigation that IS the product. Mobile-first means the phone
 * gets the pattern that suits a thumb, not a scaled-down desktop.
 *
 * ⭐ The height chain — `h-full` from <html> down, `min-h-0` on every flex parent — is what lets a
 * view scroll its own list while the top bar and the bottom tab bar stay put. A view that wants a
 * scrolling region should own `min-h-0 flex-1 overflow-y-auto` itself, as the backlog does.
 */
export function AppShell() {
  const { projectId, select } = useProjectSelection();

  return (
    /* Vocabularies and projects are AUTHENTICATED endpoints and are fetched here, inside
       RequireAuth — asking for them from the login screen returns a 401 that means nothing. */
    <VocabularyProvider>
      <ProjectsProvider>
        <div className="flex h-full min-h-0 flex-col">
          <TopBar
            nav={<ViewNav projectId={projectId} />}
            switcher={<ProjectSwitcher projectId={projectId} onSelect={select} />}
          />

          <div className="flex min-h-0 flex-1">
            <ProjectRail projectId={projectId} onSelect={select} />
            <main className="flex min-h-0 min-w-0 flex-1 flex-col">
              {/* ⭐ From here down, a view's own `<TopBar/>` renders nothing — see chrome-context.ts.
                  The shell has already drawn the header, the rail and the tab bar exactly once. */}
              <ShellChromeContext.Provider value={true}>
                <Outlet />
              </ShellChromeContext.Provider>
            </main>
          </div>

          <BottomTabBar projectId={projectId} />
        </div>
      </ProjectsProvider>
    </VocabularyProvider>
  );
}
